"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "@hackclub/icons";
import type { SparkBoxItem } from "@/lib/spark-box-manifest";
import { searchSparks } from "@/lib/spark-box-search";
import { SparkBoxSidebar } from "./SparkBoxSidebar";
import { SparkBoxCard } from "./SparkBoxCard";
import { AddSparkModal } from "./AddSparkModal";
import { CuratedDeleteAction } from "./CuratedDeleteAction";
import { familyIcon } from "./spark-families";
import type { CuratedDraft } from "@/app/api/spark-factory/drafts/route";
import type { SparkApp } from "@/app/api/spark-apps/route";

const FAMILY_COLOR: Record<string, SparkBoxItem["color"]> = {
  community: "primary",
  family: "primary",
  wellbeing: "secondary",
  connection: "secondary",
  stewardship: "accent",
};

function titleCase(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : "";
}

/** Kindness Sparks apps (GET /api/spark-apps → upstream /api/apps/all) shaped
 *  as cards for the "All Sparks" filter. Built-in apps open on
 *  kindnesssparks.org at /{family}?{app-id}, the same links the manifest uses;
 *  created apps have no confirmed page there yet, so they don't link. */
function appToItem(a: SparkApp): SparkBoxItem {
  const family = titleCase(a.family);
  return {
    name: a.name,
    title: family ? `${family} Spark` : "Spark",
    description: a.purpose,
    icon: familyIcon(a.family),
    external: a.built_in,
    url:
      a.built_in && a.family
        ? `https://www.kindnesssparks.org/${a.family}?${a.app_id.replace(/_/g, "-")}`
        : "",
    forUseBy: "",
    category: a.built_in ? "Built-in" : "Community created",
    color: FAMILY_COLOR[a.family] ?? "primary",
  };
}

/** Spark factory drafts (GET /api/spark-factory/drafts) shaped as cards for
 *  the "Curated Sparks" filter. Drafts have no description, so the idea the
 *  submitter typed stands in for one. */
type CuratedItem = SparkBoxItem & { appId: string };

function draftToItem(d: CuratedDraft): CuratedItem {
  const family = titleCase(d.family);
  return {
    appId: d.app_id,
    name: d.name,
    title: family ? `${family} Spark` : "Spark",
    description: d.idea,
    icon: familyIcon(d.family),
    external: false,
    url: "",
    forUseBy: "",
    category: "Curated",
    color: "accent",
  };
}

/** Literal port of the Sparks Box app's homepage (pages/[slug].js in the
 *  separate spark-box repo) — everything below its sticky top header, which
 *  is intentionally excluded here since this page already has its own
 *  Navbar/Footer via SiteLayout. Data, search ranking, copy, layout and
 *  interaction all match the source 1:1; only the styling technology changed
 *  (theme-ui `sx` → plain CSS, scoped under .sbx-root so it can't leak into
 *  or collide with this site's own design tokens). */

/** How long the "added successfully" toast stays; its bottom bar runs for the same time. */
const TOAST_MS = 4000;

export function SparkBoxExact({
  items,
  fontClassName,
}: {
  items: SparkBoxItem[];
  /** The source app's theme resolves ALL of its theme-ui font keys — body,
   *  heading, and even the one it calls "mono" — to the same Nunito stack
   *  (see spark-box's pages/_app.js). This applies that same font via
   *  next/font/google; nothing here uses a real monospace typeface, matching
   *  the source exactly despite what some of its own class/prop names imply. */
  fontClassName?: string;
}) {
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [addSparkOpen, setAddSparkOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; kind: "success" | "error" } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast]);
  const [curated, setCurated] = useState<CuratedItem[]>([]);
  const [curatedState, setCuratedState] = useState<"loading" | "ready" | "error">("loading");
  // null until /api/spark-apps answers; on failure "All Sparks" keeps showing
  // the local manifest so the page is never empty.
  const [allApps, setAllApps] = useState<SparkBoxItem[] | null>(null);

  const loadAllApps = useCallback(async () => {
    try {
      const res = await fetch("/api/spark-apps", { cache: "no-store" });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { apps: SparkApp[] };
      setAllApps(data.apps.map(appToItem));
    } catch {
      // Keep whatever is showing (manifest, or the last good list).
    }
  }, []);

  const loadCurated = useCallback(async () => {
    setCuratedState("loading");
    try {
      const res = await fetch("/api/spark-factory/drafts", { cache: "no-store" });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { drafts: CuratedDraft[] };
      setCurated(data.drafts.map(draftToItem));
      setCuratedState("ready");
    } catch {
      setCuratedState("error");
    }
  }, []);

  // Fetched on page load so the sidebar counts are always shown, and again
  // each time a filter is selected so its list is current.
  useEffect(() => {
    void loadAllApps();
    void loadCurated();
  }, [loadAllApps, loadCurated]);

  useEffect(() => {
    if (activeFilter === "all") void loadAllApps();
    if (activeFilter === "curated") void loadCurated();
  }, [activeFilter, loadAllApps, loadCurated]);

  const allList = useMemo(() => allApps ?? items ?? [], [allApps, items]);

  const filteredItems = useMemo(() => {
    let list = items || [];
    if (activeFilter === "all") list = allList;
    if (activeFilter === "curated") list = curated;
    return searchSparks(list, query);
  }, [items, allList, curated, activeFilter, query]);

  const counts = {
    all: allList.length,
    our: (items || []).length,
    curated: curated.length,
  };

  return (
    <div className={`sbx-root${fontClassName ? ` ${fontClassName}` : ""}`}>
      {/* Hero */}
      <div className="sbx-hero">
        <div className="sbx-hero__dots" aria-hidden="true" />
        <div className="sbx-hero__inner">
          <div className="sbx-hero__col">
            <div className="sbx-kicker">
              <Icon glyph="bolt" size={12} style={{ color: "var(--sbx-moss)" }} />
              <span>Tools that make you kind</span>
            </div>

            <h1 className="sbx-h1">
              Small tools.
              <br />
              Sparks <em className="sbx-h1__em">big</em> kindness.
            </h1>

            <p className="sbx-hero__sub">
              A growing toolbox of &quot;sparks&quot; — small, specific prompts and gestures you can
              act on today. Search the library, browse by category, or build one of your own.
            </p>

            <search>
              <form
                className="sbx-search"
                onSubmit={(e) => {
                  e.preventDefault();
                  document.getElementById("sbx-browse")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <Icon
                  glyph="search"
                  size={17}
                  style={{ color: "var(--sbx-ink-faint)", flexShrink: 0 }}
                />
                <input
                  type="search"
                  aria-label="Search sparks"
                  placeholder='Search sparks — "coffee", "check-in", "note"…'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="sbx-search__input"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => setQuery("")}
                    className="sbx-search__clear"
                  >
                    <Icon glyph="view-close" size={20} />
                  </button>
                )}
                <button type="submit" className="sbx-search__submit">
                  Search
                </button>
              </form>
            </search>
          </div>

          <div className="sbx-hero__illustration">
            <img src="/hero-illustration.svg" alt="Kindness Toolbox illustration" />
          </div>
        </div>
      </div>

      {/* Browse */}
      <div id="sbx-browse" className="sbx-browse">
        <div className="sbx-browse__grid">
          <div className="sbx-browse__sidebar">
            <SparkBoxSidebar
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              counts={counts}
              onBuildSpark={() => setAddSparkOpen(true)}
            />
          </div>

          <div className="sbx-browse__main">
            <div className="sbx-results">
              <span>
                <b>{filteredItems.length}</b> spark{filteredItems.length === 1 ? "" : "s"}{" "}
                {query.trim() ? "matching your search" : "in the toolbox"}
              </span>
            </div>

            {filteredItems.length > 0 ? (
              <div className="sbx-grid">
                {filteredItems.map((item) => (
                  <SparkBoxCard
                    key={`${activeFilter}::${item.category}::${item.name}`}
                    item={item}
                    footerAction={
                      activeFilter === "curated" && "appId" in item ? (
                        <CuratedDeleteAction
                          appId={(item as CuratedItem).appId}
                          name={item.name}
                          onDeleted={() => {
                            const appId = (item as CuratedItem).appId;
                            setCurated((list) => list.filter((c) => c.appId !== appId));
                            setToast({ message: `"${item.name}" was deleted.`, kind: "success" });
                            void loadAllApps();
                          }}
                          onError={(message) => setToast({ message, kind: "error" })}
                        />
                      ) : undefined
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="sbx-empty">
                <div className="sbx-empty__icon">
                  <Icon glyph="bolt" size={20} />
                </div>
                <h3 className="sbx-empty__title">
                  {activeFilter === "curated" && !query.trim()
                    ? curatedState === "loading"
                      ? "Loading curated sparks…"
                      : curatedState === "error"
                        ? "Curated sparks are unavailable"
                        : "No curated sparks yet"
                    : query.trim()
                      ? `No sparks match "${query.trim()}"`
                      : "No sparks found"}
                </h3>
                <p className="sbx-empty__desc">
                  {activeFilter === "curated" && !query.trim()
                    ? curatedState === "error"
                      ? "We couldn't load them right now. Please try again later."
                      : curatedState === "loading"
                        ? ""
                        : "Curated sparks will appear here once they are added."
                    : query.trim()
                      ? "Every word has to appear somewhere in the spark. Try fewer words, or a broader one."
                      : "Try adjusting your search or filter."}
                </p>
                {query.trim() && (
                  <button type="button" onClick={() => setQuery("")} className="sbx-empty__clear">
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <AddSparkModal
        open={addSparkOpen}
        onClose={() => setAddSparkOpen(false)}
        onSaved={(name) => {
          setToast({ message: `"${name}" was added successfully.`, kind: "success" });
          void loadCurated();
          void loadAllApps();
        }}
      />

      {toast && (
        <output className={`sbx-toast${toast.kind === "error" ? " sbx-toast--error" : ""}`}>
          <Icon
            glyph={toast.kind === "error" ? "important" : "checkmark"}
            size={20}
            style={{ color: "var(--sbx-toast-accent)", flexShrink: 0 }}
          />
          <span>{toast.message}</span>
          <span
            className="sbx-toast__bar"
            aria-hidden="true"
            style={{ animationDuration: `${TOAST_MS}ms` }}
          />
        </output>
      )}

      <style>{`
        .sbx-toast {
          --sbx-toast-accent: var(--sbx-moss);
          position: fixed;
          top: 24px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 10px;
          max-width: calc(100vw - 32px);
          padding: 12px 18px;
          background: var(--sbx-paper-raised);
          border: 1px solid var(--sbx-toast-accent);
          border-radius: 12px;
          box-shadow: var(--sbx-shadow-lift);
          font-size: 14px;
          font-weight: 700;
          color: var(--sbx-ink);
          animation: sbx-toast-in 0.2s ease both;
          overflow: hidden;
        }

        .sbx-toast--error {
          --sbx-toast-accent: var(--sbx-ember-deep);
        }

        /* Countdown line: shrinks from the right edge toward the left. */
        .sbx-toast__bar {
          position: absolute;
          left: 0;
          bottom: 0;
          height: 3px;
          width: 100%;
          background: var(--sbx-toast-accent);
          transform-origin: left center;
          animation-name: sbx-toast-bar;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
        }

        @keyframes sbx-toast-bar {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }

        @keyframes sbx-toast-in {
          from { opacity: 0; transform: translate(-50%, -8px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }

        .sbx-root {
          --sbx-paper: #fffbf5;
          --sbx-paper-raised: #ffffff;
          --sbx-ink: #2d2a26;
          --sbx-ink-soft: #8a8078;
          --sbx-ink-faint: #b8afa6;
          --sbx-line: #f3e4d2;
          --sbx-ember: #ff6b6b;
          --sbx-ember-deep: #f4511e;
          --sbx-ember-tint: #fff1ec;
          --sbx-moss: #20c997;
          --sbx-moss-tint: #eafbf5;
          --sbx-peri: #4dabf7;
          --sbx-peri-tint: #eef5ff;
          --sbx-cream-tint: #fff8f0;
          --sbx-shadow-card: 0 3px 10px rgba(45, 42, 38, 0.08);
          --sbx-shadow-hover: 0 10px 26px rgba(255, 107, 107, 0.14);
          --sbx-shadow-soft: 0 10px 26px rgba(255, 107, 107, 0.14);
          --sbx-shadow-lift: 0 5px 14px rgba(45, 42, 38, 0.14);
          --sbx-radius-m: 14px;
          --sbx-radius-s: 8px;

          background: var(--sbx-paper);
          color: var(--sbx-ink);
        }

        .sbx-root * {
          box-sizing: border-box;
        }

        /* Hero */
        .sbx-hero {
          position: relative;
          /* This site's Navbar is fixed/absolute (floats over content, unlike
             the source app's own in-flow sticky header, which is excluded
             here) — clamp() matches the clearance other non-hero pages in
             this codebase use so content doesn't render underneath it. */
          padding: clamp(96px, 11vw, 132px) 0 64px;
          overflow: hidden;
        }

        .sbx-hero__dots {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(var(--sbx-line) 1.4px, transparent 1.4px);
          background-size: 22px 22px;
          background-position: -10px -10px;
          mask-image: linear-gradient(to bottom, black 55%, transparent 100%);
          -webkit-mask-image: linear-gradient(to bottom, black 55%, transparent 100%);
          pointer-events: none;
        }

        .sbx-hero__inner {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 40px;
          animation: sbx-rise 0.6s ease both;
        }

        @keyframes sbx-rise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media screen and (max-width: 900px) {
          .sbx-hero__inner { flex-direction: column; }
        }

        .sbx-hero__col {
          max-width: 640px;
          flex: 1 1 55%;
        }

        .sbx-kicker {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 18px;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--sbx-moss);
        }

        .sbx-h1 {
          font-weight: 800;
          font-size: 31px;
          line-height: 1.06;
          letter-spacing: -0.015em;
          margin: 0 0 18px;
          color: var(--sbx-ink);
        }

        @media screen and (min-width: 480px) {
          .sbx-h1 { font-size: 38px; }
        }

        @media screen and (min-width: 900px) {
          .sbx-h1 { font-size: 52px; }
        }

        .sbx-h1__em {
          font-style: italic;
          font-weight: 800;
          color: var(--sbx-ember-deep);
        }

        .sbx-hero__sub {
          font-size: 17px;
          font-weight: 600;
          line-height: 1.6;
          color: var(--sbx-ink-soft);
          max-width: 480px;
          margin: 0 0 34px;
        }

        .sbx-search {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--sbx-paper-raised);
          border: 1px solid var(--sbx-line);
          border-radius: 999px;
          padding: 6px 8px 6px 20px;
          max-width: 520px;
          box-shadow: var(--sbx-shadow-card);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .sbx-search:focus-within {
          border-color: var(--sbx-ember-deep);
          box-shadow: 0 0 0 4px var(--sbx-ember-tint);
        }

        .sbx-search__input {
          border: none;
          outline: none;
          background: transparent;
          font-size: 15px;
          font-weight: 600;
          color: var(--sbx-ink);
          flex: 1;
          min-width: 0;
          padding: 8px 0;
        }

        .sbx-search__input::placeholder {
          color: var(--sbx-ink-faint);
        }

        .sbx-search__input::-webkit-search-cancel-button {
          display: none;
        }

        .sbx-search__clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 26px;
          height: 26px;
          border: none;
          border-radius: 999px;
          background: transparent;
          color: var(--sbx-ink-faint);
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .sbx-search__clear:hover {
          background: var(--sbx-cream-tint);
          color: var(--sbx-ink);
        }

        .sbx-search__submit {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 18px;
          border-radius: 999px;
          font-size: 13.5px;
          font-weight: 800;
          border: 1px solid transparent;
          color: #ffffff;
          background: linear-gradient(180deg, var(--sbx-ember) 0%, var(--sbx-ember-deep) 100%);
          box-shadow: var(--sbx-shadow-soft);
          white-space: nowrap;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }

        .sbx-search__submit:hover {
          transform: translateY(-1px);
          box-shadow: var(--sbx-shadow-lift);
        }

        .sbx-search__submit:active {
          transform: translateY(0);
        }

        .sbx-hero__illustration {
          flex: 1 1 40%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .sbx-hero__illustration img {
          width: 100%;
          max-width: 460px;
          height: auto;
          filter: drop-shadow(0 4px 24px rgba(255, 107, 107, 0.14));
        }

        @media screen and (max-width: 900px) {
          .sbx-hero__illustration { display: none; }
        }

        /* Browse */
        .sbx-browse {
          padding: 8px 0 100px;
        }

        .sbx-browse__grid {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 32px;
          display: grid;
          grid-template-columns: 1fr;
          gap: 22px;
          align-items: start;
        }

        @media screen and (min-width: 900px) {
          .sbx-browse__grid {
            grid-template-columns: 216px 1fr;
            gap: 48px;
          }
        }

        .sbx-results {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 18px;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--sbx-ink-faint);
        }

        .sbx-results b {
          color: var(--sbx-ink);
          font-weight: 800;
        }

        .sbx-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(232px, 1fr));
          gap: 18px;
        }

        /* Sidebar */
        .sbx-sidebar {
          position: sticky;
          top: 24px;
        }

        @media screen and (max-width: 900px) {
          .sbx-sidebar { position: static; }
        }

        .sbx-sidebar__label {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 14px;
          font-size: 11.5px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--sbx-ink-faint);
        }

        .sbx-sidebar__list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .sbx-sidebar__item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          width: 100%;
          text-align: left;
          font: inherit;
          background: transparent;
          border: none;
          border-left: 2px solid transparent;
          padding: 10px 12px;
          border-radius: var(--sbx-radius-s);
          font-size: 14.5px;
          font-weight: 700;
          color: var(--sbx-ink-soft);
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }

        .sbx-sidebar__item:hover {
          background: var(--sbx-cream-tint);
          color: var(--sbx-ink);
        }

        .sbx-sidebar__item--active {
          background: var(--sbx-ember-tint);
          border-left: 2px solid var(--sbx-ember-deep);
          font-weight: 800;
          color: var(--sbx-ember-deep);
        }

        .sbx-sidebar__item--active:hover {
          background: var(--sbx-ember-tint);
          color: var(--sbx-ember-deep);
        }

        .sbx-sidebar__count {
          font-size: 11.5px;
          color: var(--sbx-ink-faint);
        }

        .sbx-sidebar__item--active .sbx-sidebar__count {
          color: var(--sbx-ember-deep);
        }

        .sbx-sidebar__idea {
          margin-top: 22px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 18px;
          border: 1.5px dashed var(--sbx-line);
          border-radius: var(--sbx-radius-m);
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .sbx-sidebar__idea:hover {
          border-color: var(--sbx-moss);
          background: var(--sbx-moss-tint);
        }

        .sbx-sidebar__idea-title {
          font-size: 14px;
          font-weight: 800;
          color: var(--sbx-ink);
          margin: 0;
        }

        .sbx-sidebar__idea-desc {
          font-size: 12.5px;
          font-weight: 600;
          line-height: 1.5;
          color: var(--sbx-ink-soft);
          margin: 0;
        }

        .sbx-sidebar__idea-link {
          display: inline-block;
          font: inherit;
          font-size: 12.5px;
          font-weight: 700;
          color: var(--sbx-moss);
          margin-top: 2px;
          text-decoration: none;
          background: none;
          border: none;
          padding: 0;
          text-align: left;
          cursor: pointer;
        }

        /* Card — a <button> under the hood (keyboard-operable), reset to look
           like the plain panel the source app's clickable <div> did. */
        .sbx-card {
          display: flex;
          flex-direction: column;
          width: 100%;
          text-align: left;
          font: inherit;
          color: inherit;
          background: var(--sbx-paper-raised);
          border: 1px solid var(--sbx-line);
          border-radius: var(--sbx-radius-m);
          padding: 20px;
          box-shadow: var(--sbx-shadow-card);
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
        }

        .sbx-card:hover {
          transform: translateY(-3px);
          box-shadow: var(--sbx-shadow-hover);
        }

        .sbx-card:hover .sbx-card__try {
          color: var(--sbx-ink);
          gap: 7px;
        }

        .sbx-card__top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 14px;
        }

        .sbx-card__icon {
          width: 38px;
          height: 38px;
          flex-shrink: 0;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
        }

        .sbx-card__eyebrow {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          text-align: right;
          line-height: 1.3;
        }

        .sbx-card__heading {
          font-weight: 800;
          font-size: 18.5px;
          line-height: 1.25;
          margin: 0 0 8px;
          color: var(--sbx-ink);
        }

        .sbx-card__desc {
          font-size: 13.5px;
          font-weight: 600;
          line-height: 1.55;
          color: var(--sbx-ink-soft);
          margin: 0;
          flex: 1;
        }

        .sbx-card__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 18px;
          padding-top: 14px;
          border-top: 1px solid var(--sbx-line);
        }

        .sbx-card__category {
          font-size: 11.5px;
          font-weight: 800;
          padding: 4px 10px;
          border-radius: 999px;
        }

        .sbx-card__try {
          font-size: 12.5px;
          font-weight: 800;
          color: var(--sbx-ink-soft);
          display: flex;
          align-items: center;
          gap: 4px;
          transition: gap 0.15s ease, color 0.15s ease;
        }

        .sbx-card--static:hover {
          transform: none;
          box-shadow: var(--sbx-shadow-card);
        }

        /* Delete on Curated Sparks cards — quiet until asked. */
        .sbx-del {
          font: inherit;
          font-size: 12.5px;
          font-weight: 800;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-left: auto;
          background: transparent;
          color: var(--sbx-ink-faint);
          padding: 4px 8px 4px 4px;
          border-radius: 999px;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .sbx-del:hover,
        .sbx-del:focus-visible {
          color: var(--sbx-ember-deep);
          background: var(--sbx-ember-tint);
        }

        /* Delete confirmation popup. Tailwind preflight zeroes margins, which
           strips the UA's margin: auto centring for modal dialogs — restore it. */
        .sbx-confirm {
          position: fixed;
          inset: 0;
          margin: auto;
          height: fit-content;
          width: min(380px, calc(100vw - 32px));
          padding: 26px 24px 22px;
          border: none;
          border-radius: 20px;
          background: var(--sbx-paper-raised);
          color: var(--sbx-ink);
          text-align: center;
          box-shadow: 0 20px 50px rgba(45, 42, 38, 0.25);
        }

        .sbx-confirm::backdrop {
          background: rgba(45, 42, 38, 0.45);
        }

        .sbx-confirm[open] {
          animation: sbx-confirm-in 0.18s ease both;
        }

        @keyframes sbx-confirm-in {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }

        .sbx-confirm__icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 14px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--sbx-ember-tint);
          color: var(--sbx-ember-deep);
        }

        .sbx-confirm__title {
          font-size: 18px;
          font-weight: 800;
          margin: 0 0 6px;
        }

        .sbx-confirm__text {
          font-size: 13.5px;
          font-weight: 600;
          line-height: 1.55;
          color: var(--sbx-ink-soft);
          margin: 0 0 20px;
        }

        .sbx-confirm__actions {
          display: flex;
          gap: 10px;
        }

        .sbx-confirm__cancel,
        .sbx-confirm__delete {
          flex: 1;
          font: inherit;
          font-size: 14px;
          font-weight: 800;
          padding: 11px 16px;
          border-radius: 999px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: background 0.15s ease;
        }

        .sbx-confirm__cancel {
          background: transparent;
          border: 1px solid var(--sbx-line);
          color: var(--sbx-ink);
        }

        .sbx-confirm__cancel:hover:not(:disabled) {
          background: var(--sbx-cream-tint);
        }

        .sbx-confirm__delete {
          background: var(--sbx-ember-deep);
          border: 1px solid var(--sbx-ember-deep);
          color: #fff;
        }

        .sbx-confirm__delete:hover:not(:disabled) {
          background: #d84315;
        }

        .sbx-confirm__cancel:disabled,
        .sbx-confirm__delete:disabled {
          opacity: 0.7;
          cursor: default;
        }

        .sbx-confirm__spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.45);
          border-top-color: #fff;
          border-radius: 50%;
          animation: sbx-confirm-spin 0.8s linear infinite;
        }

        @keyframes sbx-confirm-spin {
          to { transform: rotate(360deg); }
        }

        /* Empty state */
        .sbx-empty {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          border: 1.5px dashed var(--sbx-line);
          border-radius: var(--sbx-radius-m);
          padding: 40px;
          text-align: left;
          min-height: 200px;
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .sbx-empty:hover {
          border-color: var(--sbx-moss);
          background: var(--sbx-moss-tint);
        }

        .sbx-empty__icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--sbx-moss-tint);
          color: var(--sbx-moss);
          margin-bottom: 14px;
        }

        .sbx-empty__title {
          font-weight: 800;
          font-size: 18.5px;
          line-height: 1.25;
          margin: 0 0 8px;
          color: var(--sbx-ink);
        }

        .sbx-empty__desc {
          font-size: 13.5px;
          font-weight: 600;
          line-height: 1.55;
          color: var(--sbx-ink-soft);
          margin: 0;
        }

        .sbx-empty__clear {
          margin-top: 14px;
          padding: 0;
          border: none;
          background: transparent;
          font-size: 12.5px;
          font-weight: 700;
          color: var(--sbx-moss);
          cursor: pointer;
        }

        .sbx-empty__clear:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
